<admin-app-header></admin-app-header>

<div class="monthly-dashboard-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="page-header__title">
                <h1><i class="fa-solid fa-calculator"></i> Monthly Dashboard</h1>
                <p class="page-header__subtitle">Accounting overview of invoicing, service fees, gateway fees, and net revenue</p>
            </div>
        </div>

        <!-- Date Range Filter -->
        <div class="row mb-4">
            <form class="row" [formGroup]="filterForm">
                <div class="col-md-2">
                    <label for="filterDateRange" class="form-label">Date Range</label>
                    <select class="form-select" formControlName="filterDateRange">
                        <option *ngFor="let option of dateRangeOptions" [value]="option.value">{{ option.label }}</option>
                    </select>
                </div>

                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label for="customStartDate" class="form-label">Start Date</label>
                    <input type="date" class="form-control" formControlName="customStartDate" />
                </div>

                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label for="customEndDate" class="form-label">End Date</label>
                    <input type="date" class="form-control" formControlName="customEndDate" />
                </div>
            </form>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading dashboard data...</p>
        </div>

        <!-- Dashboard Content -->
        <div *ngIf="!isLoading">
            <!-- Top Row: Invoiced, Overdue, Paid -->
            <div class="summary-cards summary-cards--three">
                <div class="summary-card summary-card--info">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-file-invoice-dollar"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.totalInvoicedAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Total Invoiced</span>
                        <span class="summary-card__subtext">{{ metrics.totalInvoicedCount }} invoices generated</span>
                    </div>
                </div>

                <div class="summary-card summary-card--overdue">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-clock-rotate-left"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.totalOverdueAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Overdue</span>
                        <span class="summary-card__subtext">{{ metrics.totalOverdueCount }} invoices past due date</span>
                    </div>
                </div>

                <div class="summary-card summary-card--success">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-circle-check"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.totalPaidAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Total Paid</span>
                        <span class="summary-card__subtext">{{ metrics.totalPaidCount }} invoices paid</span>
                    </div>
                </div>
            </div>

            <!-- Mini Row: Due, Cancelled, Breeched -->
            <div class="mini-cards">
                <div class="mini-card">
                    <i class="fa-solid fa-hourglass-half mini-card__icon mini-card__icon--due"></i>
                    <span class="mini-card__value">{{ metrics.totalDueAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                    <span class="mini-card__label">Due</span>
                    <span class="mini-card__count">{{ metrics.totalDueCount }} invoices</span>
                </div>
                <div class="mini-card">
                    <i class="fa-solid fa-ban mini-card__icon mini-card__icon--cancelled"></i>
                    <span class="mini-card__value">{{ metrics.totalCancelledAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                    <span class="mini-card__label">Cancelled</span>
                    <span class="mini-card__count">{{ metrics.totalCancelledCount }} invoices</span>
                </div>
                <div class="mini-card">
                    <i class="fa-solid fa-triangle-exclamation mini-card__icon mini-card__icon--breeched"></i>
                    <span class="mini-card__value">{{ metrics.totalBreechedAmount | currency: 'R ':'symbol':'1.2-2' }}</span>
                    <span class="mini-card__label">Breeched</span>
                    <span class="mini-card__count">{{ metrics.totalBreechedCount }} invoices</span>
                </div>
            </div>

            <!-- Bottom Row: Service Fee, Gateway Fees, Net Revenue -->
            <div class="summary-cards summary-cards--three">
                <div class="summary-card summary-card--warning">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-percent"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.totalServiceFee | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Total Service Fee</span>
                        <span class="summary-card__subtext summary-card__subtext--tight" *ngFor="let bucket of serviceFeeBreakdown">
                            {{ bucket.rate }}%: {{ bucket.feeAmount | currency: 'R ':'symbol':'1.2-2' }} ({{ bucket.invoiceCount }} invoices)
                        </span>
                    </div>
                </div>

                <div class="summary-card summary-card--danger">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-credit-card"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.totalGatewayFees | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Total Gateway Fees</span>
                        <span class="summary-card__subtext summary-card__subtext--tight">Card: {{ metrics.gatewayFeesByType.paygate | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__subtext summary-card__subtext--tight">EFT: {{ metrics.gatewayFeesByType.eft | currency: 'R ':'symbol':'1.2-2' }}</span>
                    </div>
                </div>

                <div class="summary-card summary-card--profit">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-chart-line"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ metrics.grossProfit | currency: 'R ':'symbol':'1.2-2' }}</span>
                        <span class="summary-card__label">Net Revenue</span>
                        <span class="summary-card__subtext">Service fee minus gateway fees</span>
                        <a class="potential-toggle" (click)="showPotentialProfit = !showPotentialProfit">
                            <i class="fa-solid fa-circle-info"></i> {{ showPotentialProfit ? 'Hide' : 'Potential net revenue if all invoices paid' }}
                        </a>
                        <div class="potential-info" *ngIf="showPotentialProfit">
                            <span class="potential-info__value">{{ metrics.potentialGrossProfit | currency: 'R ':'symbol':'1.2-2' }}</span>
                            <span class="potential-info__detail">Service fee: {{ metrics.potentialServiceFee | currency: 'R ':'symbol':'1.2-2' }}</span>
                            <span class="potential-info__detail">Gateway fees: {{ metrics.potentialGatewayFees | currency: 'R ':'symbol':'1.2-2' }}</span>
                            <span class="potential-info__detail">Assumes all invoices paid</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Rate Info -->
            <div class="rate-info-card">
                <div class="rate-info-card__header">
                    <i class="fa-solid fa-info-circle"></i>
                    <h3>Rate Configuration</h3>
                </div>
                <div class="rate-info-card__body">
                    <div class="rate-item">
                        <span class="rate-item__label">PayGate / PayFast gateway fee</span>
                        <span class="rate-item__value">{{ globalVariables.paygateGatewayFeePercentage }}%</span>
                    </div>
                    <div class="rate-item">
                        <span class="rate-item__label">EFT gateway fee</span>
                        <span class="rate-item__value">{{ globalVariables.eftGatewayFeePercentage }}%</span>
                    </div>
                    <div class="rate-item">
                        <span class="rate-item__label">Default service commission</span>
                        <span class="rate-item__value">15% (overridden per merchant)</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
